'use client';

import type React from 'react';
import { useState } from 'react';

import type { ChargebeeEvent } from '@/types/events';
import { formatEventType } from '@/constant/eventTypes';
import { formatSource } from '@/constant/eventSources';
import { format } from 'date-fns';
import { Button } from '@components/components/ui/button';
import { config } from '@/config';
import { explorerAnalytics } from '@/lib/analytics';
import { copyToClipboard } from '@/lib/copyToClipboard';
import { useExplorer } from '@/hooks/useExplorer';
import { Check, Copy, ExternalLink } from 'lucide-react';

interface EventDetailHeaderProps {
  event: ChargebeeEvent;
}

export const EventDetailHeader: React.FC<EventDetailHeaderProps> = ({
  event,
}) => {
  const { siteName } = useExplorer();
  const [copied, setCopied] = useState(false);
  const eventUrl = siteName
    ? `${config.cbProtocol}://${siteName}.${config.cbDomain}/d/events/${event.id}`
    : null;

  const copyEventId = async () => {
    try {
      await copyToClipboard(event.id);
      setCopied(true);
      explorerAnalytics.eventIdCopied({ eventType: event?.event_type });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  };

  const formattedDate = format(
    new Date(event.occurred_at * 1000),
    'MMM dd, yyyy HH:mm:ss'
  );

  // Extract a company/customer name from content if available
  const contentObj = event.content as Record<string, unknown>;
  const customer = contentObj?.customer as Record<string, unknown> | undefined;
  const companyName =
    (customer?.company as string) ?? (contentObj?.company as string) ?? null;

  return (
    <div className="border-b border-gray-200 px-4 py-4">
      {/* Event ID + type */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={copyEventId}
            className="font-mono text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 px-2 py-0.5 h-auto"
            title="Copy event ID"
          >
            {event.id}
            {copied ? (
              <Check className="h-3 w-3 text-green-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
          {eventUrl && (
            <a
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
              title="View in Chargebee"
              onClick={() =>
                explorerAnalytics.eventExternalLinkClicked({
                  source: 'event_detail_header',
                  eventType: event?.event_type,
                })
              }
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      <h2 className="mt-2 text-lg font-semibold text-gray-900">
        {formatEventType(event.event_type)}
      </h2>

      {/* Metadata row */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-500">
        {companyName && <MetadataItem label="Company" value={companyName} />}
        <MetadataItem label="Source" value={formatSource(event.source)} />
        {event.user && <MetadataItem label="User" value={event.user} />}
        <MetadataItem label="Occurred at" value={formattedDate} />
      </div>
    </div>
  );
};

const MetadataItem: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center gap-1">
    <span className="text-gray-400">{label}:</span>
    <span className="font-medium text-gray-600">{value}</span>
  </div>
);
